
import React, { useEffect, useMemo, useState } from "react";

const QUESTION_TYPES = [
  { value: "singleChoice", label: "Single Choice" },
  { value: "multipleChoice", label: "Multiple Choice" },
  { value: "statementBased", label: "Statement Based" },
  { value: "synonym", label: "Synonym" },
  { value: "antonym", label: "Antonym" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "fillInTheBlank", label: "Fill in the Blank" },
  { value: "errorDetection", label: "Error Detection" },
  { value: "sentenceImprovement", label: "Sentence Improvement" },
  { value: "phraseReplacement", label: "Phrase Replacement" },
  { value: "clozeTest", label: "Cloze Test" },
  { value: "paraJumble", label: "Para Jumble" },
  { value: "matchFollowing", label: "Match the Following" },
];

const DIFFICULTIES = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const defaultFormData = {
  subject: "",
  chapter: "",
  group: "",
  questionText: "",
  questionType: "singleChoice",
  difficulty: "medium",

  options: ["", "", "", ""],
  correctAnswer: "",

  highlightedText: "",
  statements: [],

  explanation: "",
  imageUrl: "",

  marks: 1,
  negativeMarks: 0,
  order: 0,
};

const choiceBasedTypes = [
  "singleChoice",
  "multipleChoice",
  "statementBased",
];

const usesOptions = (questionType) =>
  choiceBasedTypes.includes(questionType) ||
  [
    "synonym",
    "antonym",
    "vocabulary",
    "fillInTheBlank",
    "errorDetection",
    "sentenceImprovement",
    "phraseReplacement",
    "clozeTest",
    "paraJumble",
  ].includes(questionType);

const usesStatements = (questionType) =>
  questionType === "statementBased";

const isMultipleChoice = (questionType) =>
  questionType === "multipleChoice";

const normalizeInitialData = (initialData = {}) => ({
  ...defaultFormData,
  ...initialData,

  subject:
    initialData.subject?._id ||
    initialData.subject ||
    "",

  chapter:
    initialData.chapter?._id ||
    initialData.chapter ||
    "",

  group:
    initialData.group?._id ||
    initialData.group ||
    "",

  options:
    Array.isArray(initialData.options) &&
    initialData.options.length
      ? initialData.options
      : [...defaultFormData.options],

  statements:
    Array.isArray(initialData.statements)
      ? initialData.statements
      : [],

  correctAnswer:
    Array.isArray(initialData.correctAnswer)
      ? initialData.correctAnswer
      : initialData.correctAnswer || "",

  marks:
    initialData.marks ?? 1,

  negativeMarks:
    initialData.negativeMarks ?? 0,

  order:
    initialData.order ?? 0,
});

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm " +
  "text-gray-900 outline-none transition placeholder:text-gray-400 " +
  "focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

const labelClass =
  "mb-1.5 block text-sm font-medium text-gray-700";

const sectionClass =
  "rounded-xl border border-gray-200 bg-white p-5 shadow-sm";

const EnglishQuestionForm = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
  subjects = [],
  chapters = [],
  groups = [],
}) => {
  const [formData, setFormData] = useState(
    normalizeInitialData(initialData),
  );

  const [validationErrors, setValidationErrors] = useState({});

  const isEditMode = Boolean(initialData?._id);

  useEffect(() => {
    if (initialData) {
      setFormData(normalizeInitialData(initialData));
    }
  }, [initialData]);

  const selectedSubject = useMemo(
    () =>
      subjects.find(
        (subject) =>
          String(subject._id) === String(formData.subject),
      ),
    [subjects, formData.subject],
  );

  const filteredChapters = useMemo(() => {
    if (!formData.subject) return chapters;

    return chapters.filter(
      (chapter) =>
        String(
          chapter.subject?._id || chapter.subject,
        ) === String(formData.subject),
    );
  }, [chapters, formData.subject]);

  const filteredGroups = useMemo(() => {
    if (!formData.subject) return groups;

    return groups.filter(
      (group) =>
        String(
          group.subject?._id || group.subject,
        ) === String(formData.subject),
    );
  }, [groups, formData.subject]);

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setValidationErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleSubjectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      subject: value,
      chapter: "",
      group: "",
    }));
  };

  const handleQuestionTypeChange = (value) => {
    setFormData((prev) => {
      let correctAnswer = "";

      if (value === "multipleChoice") {
        correctAnswer = [];
      }

      return {
        ...prev,
        questionType: value,
        correctAnswer,
        statements:
          value === "statementBased"
            ? prev.statements.length
              ? prev.statements
              : ["", ""]
            : prev.statements,
      };
    });
  };

  const handleOptionChange = (index, value) => {
    setFormData((prev) => {
      const options = [...prev.options];
      options[index] = value;

      let correctAnswer = prev.correctAnswer;

      if (
        Array.isArray(correctAnswer) &&
        correctAnswer.includes(
          prev.options[index],
        )
      ) {
        correctAnswer = correctAnswer.map((answer) =>
          answer === prev.options[index]
            ? value
            : answer,
        );
      } else if (
        typeof correctAnswer === "string" &&
        correctAnswer === prev.options[index]
      ) {
        correctAnswer = value;
      }

      return {
        ...prev,
        options,
        correctAnswer,
      };
    });
  };

  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const removeOption = (index) => {
    setFormData((prev) => {
      if (prev.options.length <= 2) return prev;

      const removedOption = prev.options[index];

      let correctAnswer = prev.correctAnswer;

      if (Array.isArray(correctAnswer)) {
        correctAnswer = correctAnswer.filter(
          (answer) => answer !== removedOption,
        );
      } else if (correctAnswer === removedOption) {
        correctAnswer = "";
      }

      return {
        ...prev,
        options: prev.options.filter(
          (_, optionIndex) =>
            optionIndex !== index,
        ),
        correctAnswer,
      };
    });
  };

  const toggleCorrectAnswer = (option) => {
    setFormData((prev) => {
      if (isMultipleChoice(prev.questionType)) {
        const current = Array.isArray(
          prev.correctAnswer,
        )
          ? prev.correctAnswer
          : [];

        const exists = current.includes(option);

        return {
          ...prev,
          correctAnswer: exists
            ? current.filter(
                (answer) => answer !== option,
              )
            : [...current, option],
        };
      }

      return {
        ...prev,
        correctAnswer: option,
      };
    });
  };

  const addStatement = () => {
    setFormData((prev) => ({
      ...prev,
      statements: [...prev.statements, ""],
    }));
  };

  const updateStatement = (index, value) => {
    setFormData((prev) => {
      const statements = [...prev.statements];
      statements[index] = value;

      return {
        ...prev,
        statements,
      };
    });
  };

  const removeStatement = (index) => {
    setFormData((prev) => ({
      ...prev,
      statements: prev.statements.filter(
        (_, statementIndex) =>
          statementIndex !== index,
      ),
    }));
  };

  const validate = () => {
    const errors = {};

    if (!formData.subject) {
      errors.subject = "Subject is required.";
    }

    if (!formData.questionText.trim()) {
      errors.questionText =
        "Question text is required.";
    }

    if (!formData.questionType) {
      errors.questionType =
        "Question type is required.";
    }

    if (!formData.difficulty) {
      errors.difficulty =
        "Difficulty is required.";
    }

    if (
      formData.marks === "" ||
      Number(formData.marks) < 0
    ) {
      errors.marks =
        "Marks cannot be negative.";
    }

    if (
      formData.negativeMarks === "" ||
      Number(formData.negativeMarks) < 0
    ) {
      errors.negativeMarks =
        "Negative marks cannot be negative.";
    }

    if (usesOptions(formData.questionType)) {
      const validOptions = formData.options.filter(
        (option) => option.trim(),
      );

      if (validOptions.length < 2) {
        errors.options =
          "At least two options are required.";
      }

      if (
        formData.questionType ===
        "multipleChoice"
      ) {
        if (
          !Array.isArray(
            formData.correctAnswer,
          ) ||
          formData.correctAnswer.length === 0
        ) {
          errors.correctAnswer =
            "Select at least one correct answer.";
        }
      } else if (
        !formData.correctAnswer ||
        !String(formData.correctAnswer).trim()
      ) {
        errors.correctAnswer =
          "Select a correct answer.";
      }
    }

    if (
      usesStatements(formData.questionType)
    ) {
      const validStatements =
        formData.statements.filter(
          (statement) =>
            statement.trim(),
        );

      if (validStatements.length < 2) {
        errors.statements =
          "At least two statements are required.";
      }
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = {
      ...formData,

      questionText:
        formData.questionText.trim(),

      highlightedText:
        formData.highlightedText?.trim() ||
        null,

      explanation:
        formData.explanation?.trim() ||
        null,

      imageUrl:
        formData.imageUrl?.trim() || null,

      marks: Number(formData.marks),

      negativeMarks:
        Number(formData.negativeMarks),

      order: Number(formData.order || 0),

      options: formData.options
        .map((option) => option.trim())
        .filter(Boolean),

      statements: formData.statements
        .map((statement) =>
          statement.trim(),
        )
        .filter(Boolean),
    };

    if (
      payload.questionType ===
      "multipleChoice"
    ) {
      payload.correctAnswer =
        Array.isArray(formData.correctAnswer)
          ? formData.correctAnswer
          : [];
    }

    if (
      payload.questionType !==
      "multipleChoice" &&
      Array.isArray(payload.correctAnswer)
    ) {
      payload.correctAnswer =
        payload.correctAnswer[0] || "";
    }

    try {
      await onSubmit(payload);
    } catch (submitError) {
      // Parent mutation handler can display
      // its own error state.
      console.error(
        "English question submission failed:",
        submitError,
      );
    }
  };

  const renderError = (field) => {
    if (!validationErrors[field]) return null;

    return (
      <p className="mt-1 text-xs text-red-600">
        {validationErrors[field]}
      </p>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode
              ? "Edit English Question"
              : "Create English Question"}
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Create and configure an English
            question for the question bank.
          </p>
        </div>

        <div className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
          {isEditMode
            ? "Editing"
            : "New Question"}
        </div>
      </div>

      {/* API Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {typeof error === "string"
            ? error
            : error?.data?.message ||
              error?.message ||
              "Unable to save the question."}
        </div>
      )}

      {/* Basic Information */}
      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Question Information
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Select the subject, chapter, group,
            type, and difficulty.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Subject */}
          <div>
            <label className={labelClass}>
              Subject{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <select
              value={formData.subject}
              onChange={(event) =>
                handleSubjectChange(
                  event.target.value,
                )
              }
              className={inputClass}
            >
              <option value="">
                Select subject
              </option>

              {subjects.map((subject) => (
                <option
                  key={subject._id}
                  value={subject._id}
                >
                  {subject.name}
                </option>
              ))}
            </select>

            {renderError("subject")}
          </div>

          {/* Chapter */}
          <div>
            <label className={labelClass}>
              Chapter
            </label>

            <select
              value={formData.chapter}
              onChange={(event) =>
                updateField(
                  "chapter",
                  event.target.value,
                )
              }
              className={inputClass}
              disabled={!formData.subject}
            >
              <option value="">
                Select chapter
              </option>

              {filteredChapters.map(
                (chapter) => (
                  <option
                    key={chapter._id}
                    value={chapter._id}
                  >
                    {chapter.name}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Group */}
          <div>
            <label className={labelClass}>
              Question Group
            </label>

            <select
              value={formData.group}
              onChange={(event) =>
                updateField(
                  "group",
                  event.target.value,
                )
              }
              className={inputClass}
              disabled={!formData.subject}
            >
              <option value="">
                No group
              </option>

              {filteredGroups.map((group) => (
                <option
                  key={group._id}
                  value={group._id}
                >
                  {group.name ||
                    group.groupName ||
                    `${group.groupType || "Group"} - ${group._id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Question Type */}
          <div>
            <label className={labelClass}>
              Question Type{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <select
              value={formData.questionType}
              onChange={(event) =>
                handleQuestionTypeChange(
                  event.target.value,
                )
              }
              className={inputClass}
            >
              {QUESTION_TYPES.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </option>
              ))}
            </select>

            {renderError("questionType")}
          </div>

          {/* Difficulty */}
          <div>
            <label className={labelClass}>
              Difficulty{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <select
              value={formData.difficulty}
              onChange={(event) =>
                updateField(
                  "difficulty",
                  event.target.value,
                )
              }
              className={inputClass}
            >
              {DIFFICULTIES.map(
                (difficulty) => (
                  <option
                    key={difficulty.value}
                    value={difficulty.value}
                  >
                    {difficulty.label}
                  </option>
                ),
              )}
            </select>

            {renderError("difficulty")}
          </div>

          {/* Order */}
          <div>
            <label className={labelClass}>
              Display Order
            </label>

            <input
              type="number"
              min="0"
              value={formData.order}
              onChange={(event) =>
                updateField(
                  "order",
                  event.target.value,
                )
              }
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Question Text */}
      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Question Content
          </h3>
        </div>

        <div className="space-y-5">
          <div>
            <label className={labelClass}>
              Question Text{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <textarea
              rows={5}
              value={formData.questionText}
              onChange={(event) =>
                updateField(
                  "questionText",
                  event.target.value,
                )
              }
              placeholder="Enter the question..."
              className={`${inputClass} resize-y`}
            />

            {renderError("questionText")}
          </div>

          <div>
            <label className={labelClass}>
              Highlighted Text
            </label>

            <textarea
              rows={3}
              value={formData.highlightedText}
              onChange={(event) =>
                updateField(
                  "highlightedText",
                  event.target.value,
                )
              }
              placeholder="Optional text to highlight..."
              className={`${inputClass} resize-y`}
            />
          </div>

          <div>
            <label className={labelClass}>
              Image URL
            </label>

            <input
              type="url"
              value={formData.imageUrl}
              onChange={(event) =>
                updateField(
                  "imageUrl",
                  event.target.value,
                )
              }
              placeholder="https://example.com/image.jpg"
              className={inputClass}
            />

            {formData.imageUrl && (
              <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-2">
                <img
                  src={formData.imageUrl}
                  alt="Question preview"
                  className="max-h-48 rounded-md object-contain"
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Statements */}
      {usesStatements(
        formData.questionType,
      ) && (
        <section className={sectionClass}>
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Statements
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Add the statements used by the
                statement-based question.
              </p>
            </div>

            <button
              type="button"
              onClick={addStatement}
              className="rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100"
            >
              + Add Statement
            </button>
          </div>

          <div className="space-y-3">
            {formData.statements.map(
              (statement, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-600">
                    {index + 1}
                  </div>

                  <textarea
                    rows={2}
                    value={statement}
                    onChange={(event) =>
                      updateStatement(
                        index,
                        event.target.value,
                      )
                    }
                    placeholder={`Statement ${index + 1}`}
                    className={`${inputClass} resize-y`}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      removeStatement(index)
                    }
                    className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Remove statement"
                  >
                    ×
                  </button>
                </div>
              ),
            )}

            {!formData.statements.length && (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No statements added yet.
              </div>
            )}
          </div>

          {renderError("statements")}
        </section>
      )}

      {/* Options */}
      {usesOptions(
        formData.questionType,
      ) && (
        <section className={sectionClass}>
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Answer Options
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                {isMultipleChoice(
                  formData.questionType,
                )
                  ? "Select all correct answers."
                  : "Select the correct answer."}
              </p>
            </div>

            <button
              type="button"
              onClick={addOption}
              className="rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100"
            >
              + Add Option
            </button>
          </div>

          <div className="space-y-3">
            {formData.options.map(
              (option, index) => {
                const isCorrect =
                  Array.isArray(
                    formData.correctAnswer,
                  )
                    ? formData.correctAnswer.includes(
                        option,
                      )
                    : formData.correctAnswer ===
                      option;

                return (
                  <div
                    key={index}
                    className={`flex items-center gap-3 rounded-lg border p-3 transition ${
                      isCorrect
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        toggleCorrectAnswer(
                          option,
                        )
                      }
                      disabled={!option.trim()}
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition ${
                        isCorrect
                          ? "border-green-500 bg-green-500 text-white"
                          : "border-gray-300 bg-white text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                      }`}
                      title={
                        isMultipleChoice(
                          formData.questionType,
                        )
                          ? "Toggle correct answer"
                          : "Set as correct answer"
                      }
                    >
                      {isCorrect
                        ? "✓"
                        : String.fromCharCode(
                            65 + index,
                          )}
                    </button>

                    <input
                      type="text"
                      value={option}
                      onChange={(event) =>
                        handleOptionChange(
                          index,
                          event.target.value,
                        )
                      }
                      placeholder={`Option ${String.fromCharCode(
                        65 + index,
                      )}`}
                      className={inputClass}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        removeOption(index)
                      }
                      disabled={
                        formData.options
                          .length <= 2
                      }
                      className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                      title="Remove option"
                    >
                      ×
                    </button>
                  </div>
                );
              },
            )}
          </div>

          {validationErrors.options && (
            <p className="mt-2 text-xs text-red-600">
              {validationErrors.options}
            </p>
          )}

          {validationErrors.correctAnswer && (
            <p className="mt-2 text-xs text-red-600">
              {validationErrors.correctAnswer}
            </p>
          )}

          {formData.correctAnswer &&
            (Array.isArray(
              formData.correctAnswer,
            )
              ? formData.correctAnswer.length >
                0
              : true) && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                <span className="font-medium">
                  Correct answer:
                </span>{" "}
                {Array.isArray(
                  formData.correctAnswer,
                )
                  ? formData.correctAnswer.join(
                      ", ",
                    )
                  : formData.correctAnswer}
              </div>
            )}
        </section>
      )}

      {/* Marks */}
      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Scoring
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>
              Marks{" "}
              <span className="text-red-500">
                *
              </span>
            </label>

            <input
              type="number"
              min="0"
              step="0.25"
              value={formData.marks}
              onChange={(event) =>
                updateField(
                  "marks",
                  event.target.value,
                )
              }
              className={inputClass}
            />

            {renderError("marks")}
          </div>

          <div>
            <label className={labelClass}>
              Negative Marks
            </label>

            <input
              type="number"
              min="0"
              step="0.25"
              value={formData.negativeMarks}
              onChange={(event) =>
                updateField(
                  "negativeMarks",
                  event.target.value,
                )
              }
              className={inputClass}
            />

            {renderError("negativeMarks")}
          </div>
        </div>
      </section>

      {/* Explanation */}
      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Explanation
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Provide an explanation that students
            can see after completing the quiz.
          </p>
        </div>

        <textarea
          rows={5}
          value={formData.explanation}
          onChange={(event) =>
            updateField(
              "explanation",
              event.target.value,
            )
          }
          placeholder="Explain the correct answer..."
          className={`${inputClass} resize-y`}
        />
      </section>

      {/* Actions */}
      <div className="sticky bottom-0 z-10 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading
              ? "Saving..."
              : isEditMode
                ? "Update Question"
                : "Create Question"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EnglishQuestionForm;

